import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import SpinLoader, {
  LOADER_COLOR_DARK,
  LOADER_SIZE_LARGE,
  POSITION_ABSOLUTE,
} from "shared/components/SpinLoader";

import { useOutsideClick } from "shared/hooks/useOutsideClick";

import styles from "@platformAssets/components/InputWithSelect.module.scss";

const InputWithSelect = ({
  attribute,
  name,
  value = null,
  values,
  getValues,
  change,
  placeholder = null,
  animatePlaceholder = false,
  id = null,
  autoComplete = "off",
  className = null,
  disabled = false,
  errorText = null,
  warningText = null,
  error = false,
  type = "text",
  send,
  emptyValueError = "",
  clearError,
  clearSearch,
  setClearSearch,
}) => {
  let timeout;
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState(
    attribute && Object.keys(values).length ? values[attribute] : ""
  );
  const [item, setItem] = useState(
    attribute && Object.keys(values).length
      ? { value: attribute, label: values[attribute] }
      : null
  );
  const [loading, setLoading] = useState(false);

  /**
   * Styles for input with select
   **/
  const stylesValues =
    disabled && errorText
      ? `${styles.disabled}`
      : disabled && warningText
        ? `${styles.disabled}`
        : disabled && error
          ? `${styles.disabled} ${styles.errorDisabled}`
          : disabled
            ? `${styles.disabled} `
            : error || errorText
              ? `${styles.error}`
              : "";

  /**
   * Effect for generate list of values
   **/
  useEffect(() => {
    let i = 0;
    let newList = [];
    if (Object.keys(values).length > 0) {
      Object.keys(values).forEach((key) => {
        const label = values[key];
        const item = {
          value: key,
          label,
        };
        newList[i] = (
          <li
            className={styles.results}
            key={i}
            onClick={() => handleChangeValue(item)}
          >
            {label}
          </li>
        );
        i++;
      });
    } else if (Object.keys(values).length === 0) {
      newList[i] = <li key={i}>{t("common:noResultsFound")}</li>;
    }
    setList(newList);

    if (
      searchText === "" &&
      attribute &&
      Object.keys(values).length &&
      values[attribute]
    ) {
      setSearchText(values[attribute]);
      setItem({ value: attribute, label: values[attribute] });
    }
  }, [values]);

  /**
   * Effect for clear search value
   **/
  useEffect(() => {
    if (!clearSearch) return;

    setSearchText("");
    clearError();
    setItem(null);
    change("");
    setClearSearch(false);
  }, [clearSearch]);

  /**
   * Handle change value
   * @param {object} itemData
   **/
  const handleChangeValue = (itemData) => {
    setSearchText(itemData.label);
    setOpen(false);
    setItem(itemData);
    change(itemData.value);
  };

  /**
   * Handle click outside
   **/
  const handleClickOutside = () => {
    setOpen(false);
  };

  /**
   * Ref for outside click
   **/
  const ref = useOutsideClick(handleClickOutside);

  /**
   * Handler type action
   * @param {object} e
   **/
  const handlerType = (e) => {
    if (timeout) {
      clearTimeout(timeout);
      setLoading(true);
    }
    timeout = setTimeout(() => {
      getValues(e.target.value);
      setLoading(false);
    }, 200);

    clearError();
    setOpen(e.target.value.length >= 2);
    setSearchText(e.target.value);
    setItem(null);
    if (value) {
      change("");
    }
  };

  return (
    <div
      className={`${styles.inputWrapper} ${
        animatePlaceholder ? styles.inputAnimated : ""
      } ${className ?? ""}`}
      ref={ref}
    >
      <div className={styles.inputWrapperRelative}>
        <input
          className="hidden"
          type="hidden"
          name={name}
          value={item ? item.value : ""}
          readOnly
        />
        <input
          className={stylesValues}
          type={type}
          name={name}
          id={id ?? `${name}Id`}
          autoComplete={autoComplete}
          placeholder={placeholder ?? ""}
          disabled={disabled}
          value={searchText ?? ""}
          onChange={handlerType}
        />
        {animatePlaceholder && (
          <label
            htmlFor={id ?? `${name}Id`}
            className={styles.inputAnimatedPlaceholder}
          >
            {placeholder}
          </label>
        )}
        {open && !item && (
          <div className={styles.selectListWrap}>
            <ul className={styles.selectList}>
              {send || loading ? (
                <SpinLoader
                  color={LOADER_COLOR_DARK}
                  size={LOADER_SIZE_LARGE}
                  position={POSITION_ABSOLUTE}
                />
              ) : (
                list
              )}
            </ul>
          </div>
        )}
      </div>
      <p className={styles.errorText}>
        {!!searchText && errorText && emptyValueError
          ? emptyValueError
          : errorText}
      </p>
      <p className={styles.errorWarning}>{warningText}</p>
    </div>
  );
};

InputWithSelect.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.any,
  values: PropTypes.object.isRequired,
  getValues: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  id: PropTypes.string,
  className: PropTypes.string,
  animatePlaceholder: PropTypes.bool,
  placeholder: PropTypes.string,
  autoComplete: PropTypes.string,
  errorText: PropTypes.string,
  warningText: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  send: PropTypes.bool,
  emptyValueError: PropTypes.string,
  clearError: PropTypes.func.isRequired,
  clearSearch: PropTypes.bool.isRequired,
  setClearSearch: PropTypes.func.isRequired,
};

export default InputWithSelect;
